import { FC } from "react";
import { Domain } from "@/types/domain";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { FileText, ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface DomainElementsProps {
  domains: Domain[];
}

interface KnowledgeBase {
  title: string;
  url?: string;
}

// Representative knowledge bases for each GSA domain
const getDomainKnowledgeBases = (symbol: string): KnowledgeBase[] => {
  const kbMap: Record<string, KnowledgeBase[]> = {
    "Federal Procurement": [
      { title: "GSA Schedule 70: IT Solutions", url: "#" },
      { title: "Multiple Award Schedules (MAS) Program Guide", url: "#" },
      { title: "Simplified Acquisition Procedures Handbook", url: "#" },
      { title: "Federal Supply Schedule Best Practices", url: "#" },
      { title: "Contractor Performance Assessment Framework", url: "#" }
    ],
    "Real Estate": [
      { title: "Federal Property Management Standards", url: "#" },
      { title: "Lease Negotiation and Administration Guide", url: "#" },
      { title: "Sustainable Building Design Guidelines", url: "#" },
      { title: "Historic Property Preservation Protocols", url: "#" },
      { title: "Space Utilization Optimization Toolkit", url: "#" }
    ],
    "IT": [
      { title: "Cloud First Policy Implementation Guide", url: "#" },
      { title: "Federal Cybersecurity Framework (NIST)", url: "#" },
      { title: "AI/ML Integration for Federal Agencies", url: "#" },
      { title: "Data Governance and Privacy Compliance", url: "#" },
      { title: "IT Modernization Roadmap", url: "#" }
    ],
    "Fleet": [
      { title: "Federal Vehicle Acquisition Standards", url: "#" },
      { title: "Electric Fleet Transition Planning", url: "#" },
      { title: "GPS and Telematics Implementation", url: "#" },
      { title: "Fleet Cost Optimization Strategies", url: "#" },
      { title: "Vehicle Lifecycle Management Guide", url: "#" }
    ],
    "Supply Chain": [
      { title: "Federal Supply Chain Resilience Framework", url: "#" },
      { title: "Warehouse Operations Best Practices", url: "#" },
      { title: "Strategic Sourcing Methodologies", url: "#" },
      { title: "Global Risk Mitigation Strategies", url: "#" },
      { title: "Vendor Management Excellence", url: "#" }
    ],
    "P3": [
      { title: "Public-Private Partnership Models", url: "#" },
      { title: "P3 Contract Templates and Compliance", url: "#" },
      { title: "Risk Allocation Framework", url: "#" },
      { title: "Alternative Funding Mechanisms", url: "#" },
      { title: "Performance-Based Contracting Guide", url: "#" }
    ],
    "Workforce": [
      { title: "Federal Human Capital Strategy", url: "#" },
      { title: "Employee Training and Development Programs", url: "#" },
      { title: "DEI Implementation Toolkit", url: "#" },
      { title: "Remote Work Policy Framework", url: "#" },
      { title: "Leadership Development Pipeline", url: "#" }
    ],
    "Energy": [
      { title: "Green Procurement Standards", url: "#" },
      { title: "Net Zero Energy Building Guidelines", url: "#" },
      { title: "Energy Performance Contracting Manual", url: "#" },
      { title: "Climate Resilience Assessment Tools", url: "#" },
      { title: "Renewable Energy Integration Guide", url: "#" }
    ],
    "Grants": [
      { title: "Grant Application Process Guide", url: "#" },
      { title: "Federal Stimulus Package Overview", url: "#" },
      { title: "FEMA Disaster Recovery Grants", url: "#" },
      { title: "Cooperative Agreements Framework", url: "#" },
      { title: "Grant Impact Assessment Methodology", url: "#" }
    ],
    "Emergency": [
      { title: "Federal Disaster Response Planning", url: "#" },
      { title: "Government Resilience Framework", url: "#" },
      { title: "Emergency Procurement Procedures", url: "#" },
      { title: "Critical Infrastructure Protection", url: "#" },
      { title: "COOP Planning and Implementation", url: "#" }
    ],
    "Travel": [
      { title: "Federal Travel Regulation (FTR)", url: "#" },
      { title: "Per Diem Rate Guidelines", url: "#" },
      { title: "Travel Management Services Contract", url: "#" },
      { title: "Travel Compliance Audit Checklist", url: "#" },
      { title: "Conference Planning Best Practices", url: "#" }
    ],
    "Smart Cities": [
      { title: "Federal Smart City Strategy", url: "#" },
      { title: "IoT for Public Infrastructure", url: "#" },
      { title: "Smart Energy Grid Implementation", url: "#" },
      { title: "Digital Identity Management", url: "#" },
      { title: "Public Transit Modernization Guide", url: "#" }
    ],
    "Cybersecurity": [
      { title: "Federal Data Protection Standards", url: "#" },
      { title: "Identity and Access Management (IAM)", url: "#" },
      { title: "Continuous Monitoring Framework", url: "#" },
      { title: "Cloud Security for Federal Systems", url: "#" },
      { title: "Vendor Cyber Risk Assessment", url: "#" }
    ],
    "Vendor": [
      { title: "Industry Day Planning Guide", url: "#" },
      { title: "Vendor Certification Requirements", url: "#" },
      { title: "Small Business Set-Aside Programs", url: "#" },
      { title: "GSA Schedule Pricing Analysis", url: "#" },
      { title: "Innovation Procurement Models", url: "#" }
    ],
    "Open Data": [
      { title: "Federal Data Sharing Agreements", url: "#" },
      { title: "Open Government Data Initiative", url: "#" },
      { title: "AI-Driven Analytics Platform", url: "#" },
      { title: "Cloud Data Warehousing Solutions", url: "#" },
      { title: "GIS and Geospatial Tools", url: "#" }
    ]
  };
  
  return kbMap[symbol] || [
    { title: `${symbol} Best Practices Guide`, url: "#" },
    { title: `${symbol} Implementation Framework`, url: "#" },
    { title: `${symbol} Case Studies Collection`, url: "#" }
  ];
};

const DomainElements: FC<DomainElementsProps> = ({
  domains
}) => {
  // Fetch knowledge bases with domain associations
  const { data: domainKBs } = useQuery({
    queryKey: ['gsa-channel-kbs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('knowledge_base_domains')
        .select(`
          knowledge_base_id,
          domain_id,
          domains(name, symbol),
          knowledge_bases!inner(
            id,
            title,
            status,
            deleted_at,
            creator_id
          )
        `)
        .eq('knowledge_bases.status', 'published')
        .is('knowledge_bases.deleted_at', null);
      
      if (error) throw error;
      return data;
    }
  });

  // Get KB count for a domain
  const getKBCount = (domainName: string) => {
    if (!domainKBs) return 0;
    return domainKBs.filter(kb => 
      kb.domains?.name === domainName
    ).length;
  };

  return (
    <div className="grid grid-cols-5 gap-2">
      {domains.map(domain => {
        const knowledgeBases = getDomainKnowledgeBases(domain.symbol);
        const kbCount = getKBCount(domain.name);
        const displayName = `${domain.name} (${kbCount})`;
        
        return (
          <div key={domain.symbol} className="flex flex-col">
            <HoverCard openDelay={200} closeDelay={100}>
              <HoverCardTrigger asChild>
                <div 
                  className="bg-blue-600 text-white p-3 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:bg-black hover:shadow-lg flex flex-col items-center relative h-20 min-w-0"
                >
                  <div className="text-sm font-bold text-center leading-tight flex-1 flex items-center justify-center px-1">
                    {displayName}
                  </div>
                </div>
              </HoverCardTrigger>
              <HoverCardContent 
                side="bottom" 
                align="start"
                className="w-80 bg-white p-4 shadow-xl border border-gray-200 z-50"
              >
                <div className="space-y-1">
                  <h4 className="font-semibold text-sm mb-2">{domain.name}</h4>
                  <p className="text-xs text-gray-600 mb-3 line-clamp-2">{domain.categories}</p>
                  <div className="space-y-2">
                    {knowledgeBases.map((kb, kbIndex) => (
                      <a
                        key={kbIndex}
                        href={kb.url}
                        className="flex items-start gap-2 p-2 rounded hover:bg-gray-50 transition-colors group"
                      >
                        <FileText className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-xs flex-1 text-gray-700 group-hover:text-blue-600">
                          {kb.title}
                        </span>
                        <ExternalLink className="h-3 w-3 text-gray-400 flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    ))}
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>
          </div>
        );
      })}
    </div>
  );
};

export default DomainElements;
